// Libraries
import { MouseEventHandler } from 'react';

// Styles
import styles from './ChatMessage.module.css';

/**
 * @function ChatMessage
 * Describes a chat message
 *
 * @example
 * return (
 *   <ChatMessage
 *     type={''}
 *     user={{
 *       id: 0,
 *       username: '',
 *       avatar: ''
 *     }}
 *     showName={true}
 *     avatar={''}
 *     text={''}
 *     onClick={() => false}
 *   />
 * )
 */
function ChatMessage({
    type,
    user,
    showName,
    avatar,
    text,
    onClick,
}: ChatMessageProps): JSX.Element {
    return (
        <figure
            className={`${styles.figure} ${styles[type]}`}
            onClick={onClick}
        >
            {type !== 'client' && (
                <picture className={styles.picture}>
                    <img src={avatar} alt={user.username} />
                </picture>
            )}
            <div>
                {showName && (
                    <figcaption className={styles.caption}>
                        {user.username}
                    </figcaption>
                )}
                <blockquote className={styles.quote}>{text}</blockquote>
            </div>
        </figure>
    );
}

/**
 * @interface ChatMessageProps
 * Describes the properties required for the ChatMessage component
 *
 * @property {string} type Type of message
 * @property {object} user User data of message sender
 *   @property {string} id Unique identifier
 *   @property {string} username User name of sender
 *   @property {string} avatar User profile image of sender
 * @property {boolean} showName Whether to show name of sender
 * @property {string} text Contents of message
 */
export interface ChatMessageProps {
    type: 'server' | 'client' | 'user';
    user: {
        id: number;
        username: string;
        avatar: string;
    };
    showName: boolean;
    avatar: string;
    text: string;
    onClick: MouseEventHandler<HTMLButtonElement>;
}

// Exports
export default ChatMessage;
